import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import api from "@/lib/api";
import Logo from "@/components/Logo";
import SocialAuthButtons from "@/components/SocialAuthButtons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, Truck, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import TurnstileWidget, { useTurnstile } from "@/components/TurnstileWidget";

const BG = "https://images.unsplash.com/photo-1598175834650-13dd92cffa21?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwxfHxTbm93eSUyME1vdW50YWlucyUyMHJ1cmFsJTIwcm9hZCUyMEF1c3RyYWxpYXxlbnwwfHx8fDE3ODY0OTU2NDR8MA&ixlib=rb-4.1.0&q=85";

function routeFor(role) {
  return role === "admin" ? "/admin" : role === "driver" ? "/driver" : "/dashboard";
}

export default function Welcome() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [tab, setTab] = useState("login");
  const [loading, setLoading] = useState(false);
  const [refCode, setRefCode] = useState("");

  useEffect(() => {
    const r = params.get("ref");
    if (r) { setRefCode(r); setTab("register"); }
    if (params.get("quote") && params.get("tab") === "register") setTab("register");
  }, [params]);
  const quoteToken = params.get("quote");
  const postAuthRoute = (role) => {
    const next = sessionStorage.getItem("kc_next");
    sessionStorage.removeItem("kc_next");
    if (quoteToken && role === "customer") return `/q/${quoteToken}`;
    if (next && role === "customer" && next.startsWith("/")) return next;
    return routeFor(role);
  };

  const [li, setLi] = useState({ email: "", password: "" });
  const [rg, setRg] = useState({ name: "", email: "", phone: "", password: "", website: "" });
  const [forgot, setForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const tsLogin = useTurnstile();
  const tsSignup = useTurnstile();

  const doForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail });
      setForgotSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send reset email");
    } finally { setLoading(false); }
  };

  const doLogin = async (e) => {
    e.preventDefault();
    if (tsLogin.blocked) { toast.error("Please complete the security check"); return; }
    setLoading(true);
    try {
      const u = await login(li.email, li.password, tsLogin.token);
      toast.success(`Welcome back, ${u.name}`);
      navigate(postAuthRoute(u.role));
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed"); }
    finally { tsLogin.reset(); setLoading(false); }
  };

  const doRegister = async (e) => {
    e.preventDefault();
    if (tsSignup.blocked) { toast.error("Please complete the security check"); return; }
    setLoading(true);
    try {
      const u = await register({ ...rg, referral_code: refCode, cf_turnstile_response: tsSignup.token || "" });
      toast.success(`Account created — welcome, ${u.name}!`);
      toast.info("We've emailed you a verification link — click it to unlock booking.", { duration: 8000 });
      navigate(postAuthRoute(u.role));
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Registration failed"); }
    finally { tsSignup.reset(); setLoading(false); }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img src={BG} alt="Monaro mountain road" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-kilted-green/65" />
        <div className="absolute inset-0 flex flex-col justify-between p-10">
          <Logo light />
          <div>
            <h1 className="font-heading text-4xl font-extrabold leading-tight text-white">Parcels delivered <span className="text-kilted-gold">Promptly</span> &amp; with <span className="text-kilted-gold">Precision</span>.</h1>
            <p className="mt-3 max-w-md text-white/85">Book couriers, track parcels and manage deliveries across the Monaro region of NSW.</p>
            <div className="mt-6 flex items-center gap-2 text-sm text-white/80"><Truck className="h-4 w-4 text-kilted-gold" /> Trusted couriers · Cooma to Jindabyne</div>
            <div className="mt-5 max-w-md rounded-xl border border-kilted-gold/40 bg-white/10 p-4 backdrop-blur-sm" data-testid="canberra-run-card">
              <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-kilted-gold">
                <CalendarClock className="h-3.5 w-3.5" /> Canberra &amp; Queanbeyan run
              </p>
              <p className="mt-1.5 text-sm leading-relaxed text-white/90">
                Pickups <span className="font-bold text-kilted-gold">every morning, 8:30am–12pm</span> —
                delivered the same afternoon to Cooma and the whole Jindabyne area.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center bg-background px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="mb-8 lg:hidden"><Logo /></div>
          <h2 className="font-heading text-3xl font-extrabold tracking-tight text-kilted-green">Welcome</h2>
          <p className="mt-1 text-sm text-muted-foreground">Sign in or create an account to get started.</p>

          <Tabs value={tab} onValueChange={setTab} className="mt-6">
            <TabsList className="grid w-full grid-cols-2" data-testid="welcome-tabs">
              <TabsTrigger value="login" data-testid="tab-login">Sign in</TabsTrigger>
              <TabsTrigger value="register" data-testid="tab-register">Create account</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              {!forgot ? (
                <form onSubmit={doLogin} className="mt-4 space-y-4">
                  <div className="space-y-1.5"><Label htmlFor="li-email">Email</Label><Input id="li-email" type="email" value={li.email} onChange={(e) => setLi({ ...li, email: e.target.value })} required data-testid="login-email-input" /></div>
                  <div className="space-y-1.5"><Label htmlFor="li-pw">Password</Label><Input id="li-pw" type="password" value={li.password} onChange={(e) => setLi({ ...li, password: e.target.value })} required data-testid="login-password-input" /></div>
                  <TurnstileWidget ref={tsLogin.ref} action="login" onToken={tsLogin.setToken} onReady={tsLogin.onReady} />
                  <Button type="submit" disabled={loading || tsLogin.blocked} data-testid="login-submit-button" className="w-full gap-2 bg-kilted-green hover:bg-kilted-greenLight">{loading && <Loader2 className="h-4 w-4 animate-spin" />} Sign in</Button>
                  <button type="button" onClick={() => { setForgot(true); setForgotEmail(li.email); setForgotSent(false); }} data-testid="forgot-password-link" className="w-full text-center text-sm font-medium text-kilted-green hover:underline">
                    Forgot your password?
                  </button>
                </form>
              ) : forgotSent ? (
                <div className="mt-4 space-y-4" data-testid="forgot-sent">
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
                    If an account exists for <strong>{forgotEmail}</strong>, we've emailed a link to reset your password — and texted it to the mobile on file too. It expires in 1 hour — check your inbox, spam and messages.
                  </div>
                  <Button type="button" variant="outline" onClick={() => { setForgot(false); setForgotSent(false); }} data-testid="forgot-back-button" className="w-full">Back to sign in</Button>
                </div>
              ) : (
                <form onSubmit={doForgot} className="mt-4 space-y-4" data-testid="forgot-form">
                  <p className="text-sm text-muted-foreground">Enter your account email and we'll send you a link to reset your password.</p>
                  <div className="space-y-1.5"><Label htmlFor="fg-email">Email</Label><Input id="fg-email" type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} required data-testid="forgot-email-input" /></div>
                  <Button type="submit" disabled={loading} data-testid="forgot-submit-button" className="w-full gap-2 bg-kilted-green hover:bg-kilted-greenLight">{loading && <Loader2 className="h-4 w-4 animate-spin" />} Send reset link</Button>
                  <button type="button" onClick={() => setForgot(false)} data-testid="forgot-cancel-button" className="w-full text-center text-sm font-medium text-muted-foreground hover:underline">Back to sign in</button>
                </form>
              )}
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={doRegister} className="mt-4 space-y-4">
                <input type="text" name="website" value={rg.website} onChange={(e) => setRg({ ...rg, website: e.target.value })} tabIndex={-1} autoComplete="off" aria-hidden="true" style={{ position: "absolute", left: "-9999px", height: 0, width: 0, opacity: 0 }} />
                {refCode && (
                  <div className="rounded-lg border border-kilted-gold/40 bg-kilted-gold/10 p-3 text-sm text-kilted-green" data-testid="referral-invite-banner">
                    🎉 You've been invited! Create your account — you'll get $5 credit once your first delivery is completed.
                  </div>
                )}
                <div className="space-y-1.5"><Label htmlFor="rg-name">Full name</Label><Input id="rg-name" value={rg.name} onChange={(e) => setRg({ ...rg, name: e.target.value })} required data-testid="register-name-input" /></div>
                <div className="space-y-1.5"><Label htmlFor="rg-email">Email</Label><Input id="rg-email" type="email" value={rg.email} onChange={(e) => setRg({ ...rg, email: e.target.value })} required data-testid="register-email-input" /></div>
                <div className="space-y-1.5"><Label htmlFor="rg-phone">Mobile number</Label><Input id="rg-phone" value={rg.phone} onChange={(e) => setRg({ ...rg, phone: e.target.value })} placeholder="+61…" data-testid="register-phone-input" /></div>
                <div className="space-y-1.5"><Label htmlFor="rg-pw">Password</Label><Input id="rg-pw" type="password" minLength={6} value={rg.password} onChange={(e) => setRg({ ...rg, password: e.target.value })} required data-testid="register-password-input" /></div>
                <TurnstileWidget ref={tsSignup.ref} action="signup" onToken={tsSignup.setToken} onReady={tsSignup.onReady} />
                <Button type="submit" disabled={loading || tsSignup.blocked} data-testid="register-submit-button" className="w-full gap-2 bg-kilted-green hover:bg-kilted-greenLight">{loading && <Loader2 className="h-4 w-4 animate-spin" />} Create account</Button>
              </form>
            </TabsContent>
          </Tabs>

          <div className="my-5 flex items-center gap-3">
            <span className="h-px flex-1 bg-border" /><span className="text-xs font-medium uppercase text-muted-foreground">or continue with</span><span className="h-px flex-1 bg-border" />
          </div>
          <SocialAuthButtons />

          <div className="mt-6 rounded-lg border border-kilted-gold/40 bg-kilted-gold/10 p-3 text-xs leading-relaxed text-kilted-green lg:hidden" data-testid="canberra-run-card-mobile">
            <span className="font-bold">Canberra &amp; Queanbeyan run:</span> pickups every morning (8:30am–12pm), delivered the same afternoon to Cooma and the whole Jindabyne area.
          </div>
        </div>
      </div>
    </div>
  );
}
